/**
 * List of all the events that are tracked in the app.
 *
 * All new events should be named in snake_case like so: `verb_noun`
 */

export enum AnalyticsTrackingEvent {
  OPEN_APP = 'app open',
  FINGERPRINTJS_ERROR = 'fingerprintjs_error',
  GRAPHQL_ERROR = 'graphql_error',
  BRANDCLUB_LANDING_PAGE_LOADED = 'brandclub_landing_page_loaded',
  BRANDCLUB_LANDING_PAGE_TEXT_APP_LINK = 'brandclub_landing_page_text_app_link',
  BRANDCLUB_LANDING_PAGE_QRCODE_SCANNED = 'brandclub_landing_page_qrcode_scanned',
  JOINED_BRANDCLUB = 'joined brandclub',
  OPENED_SHARE_AFFILIATE_LINK = 'Opened share affiliate link',
  OPENED_SHARE_REFERRAL_CODE = 'Opened share referral code',
  OPENED_SHARE_PAGE_CARD_LINK = 'Opened share page card link',
  CLOSED_SHARE_AFFILIATE_LINK = 'Closed share affiliate link',
  CLOSED_SHARE_REFERRAL_CODE = 'Closed share referral code',
  CLOSED_SHARE_PAGE_CARD_LINK = 'Closed share page card link',
  SHARED_AFFILIATE_LINK = 'Shared affiliate link',
  SHARED_REFERRAL_CODE = 'Shared referral code',
  SHARED_PAGE_CARD_LINK = 'Shared page card link',
  TEST_EVENT = 'test event',
  MESSAGE_DETAIL_PAGE_OPENED = 'messageDetailPage opened',
  MESSAGE_DETAIL_PAGE_LINK_CLICKED = 'messageDetailPage link clicked',
  SELECTED_SEARCH_TERM_FROM_DROPDOWN = 'selected search term from dropdown',
  SERVER_SIDE_SYNC_MFA_CHOOSE_OPEN = 'server_side_sync_mfa_choose_open',
  SERVER_SIDE_SYNC_MFA_CHOOSE_SUCCESS = 'server_side_sync_mfa_choose_success',
  SERVER_SIDE_SYNC_MFA_CHOOSE_ERROR = 'server_side_sync_mfa_choose_error',
  SERVER_SIDE_SYNC_MFA_ENTER_OPEN = 'server_side_sync_mfa_enter_open',
  SERVER_SIDE_SYNC_MFA_ENTER_SUCCESS = 'server_side_sync_mfa_enter_success',
  SERVER_SIDE_SYNC_MFA_ENTER_ERROR = 'server_side_sync_mfa_enter_error',
  SAVE_REVIEW = 'save review',
  INVALID_REVIEW = 'invalid review',
  SAVE_REVIEW_SUCCESS = 'save review success',
  ERROR_WHILE_SAVING_REVIEW = 'error while saving review',
  BACK_REVIEW = 'back_review',
  STARTED_SUBMIT_REVIEW = 'started submit review',
  SENDING_NON_ELIGIBLE_REVIEW_TO_BRAND = 'Sending non-eligible review to brand',
  SELECTED_APP_REVIEW_OPTION = 'selected app review option',
  SKIPPED_LEAVE_APP_REVIEW = 'skipped leave app review',
  CONTINUE_REVIEW = 'continue_review',
  STARTED_SCANNING = 'Started scanning',
  ERROR_WHILE_SCANNING = 'Error while scanning',
  COMPLETED_SCANNING = 'Completed scanning',
  RETAILER_SYNC_STARTED = 'retailer_sync_started',
  RETAILER_SYNC_COMPLETED = 'retailer_sync_completed',
  DISCONNECT_RETAILER = 'disconnect_retailer',
  MOBILE_APP_SOCIAL_LINK_OPEN_PRODUCT_PAGE = 'mobileApp:socialLink:openProductPage',
  MOBILE_APP_SOCIAL_LINK_PRODUCT_NOT_FOUND = 'mobileApp:socialLink:productNotFound',
  MOBILE_APP_SOCIAL_LINK_CLOSE_RETAILER_PRODUCT_PAGE = 'mobileApp:socialLink:closeRetailerProductPage',
  MOBILE_APP_ORGANIC_OPEN_PRODUCT_PAGE = 'mobileApp:organic:openProductPage',
  MOBILE_APP_ORGANIC_PRODUCT_NOT_FOUND = 'mobileApp:organic:productNotFound',
  MOBILE_APP_ORGANIC_CLOSE_RETAILER_PRODUCT_PAGE = 'mobileApp:organic:closeRetailerProductPage',
  MOBILE_APP_PRODUCT_PAGE_SCROLL = 'mobileApp:productPage:scroll',
  WEB_APP_SOCIAL_LINK_TEXT_APP_DOWNLOAD_LINK = 'webApp:socialLink:textAppDownloadLink',
  WEB_APP_ORGANIC_ADD_TO_CART = 'webApp:organic:addToCart',
  OPEN_RETAILER_PRODUCT_PAGE = 'openRetailerProductPage',
  WEB_APP_ORGANIC_OPEN_VIEW_OFFERS = 'webApp:organic:openViewOffers',
  WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE = 'webApp:organic:openRetailerProductPage',
  WEB_APP_PRODUCT_PAGE_SCROLL = 'webApp:productPage:scroll',
  ACCOUNTS_HOME_RETAILERS = 'accounts_home_retailers',
  AUTO_SIGN_IN = 'auto sign in',
  ERROR_ON_NEXT_IN_SIGN_UP = 'error on next in sign up',
  OPENED_TERMS_OF_SERVICE = 'opened terms of service',
  OPENED_PRIVACY_POLICY = 'opened privacy policy',
  SIGN_UP = 'sign up',
  SIGN_IN = 'sign in',
  SIGN_OUT = 'sign out',
  SIGN_IN_FAILURE = 'sign in failure',
  COMPLETED_ONBOARDING = 'completed_onboarding',
  VERIFIED_PHONE_NUMBER = 'verified phone number',
  RESEND_PHONE_VERIFICATION = 'resend phone verification',
  ERROR_VERIFYING_PHONE_VERIFICATION_CODE = 'error verifying phone verification code',
  PASSWORD_RESET_ATTEMPTS = 'password reset attempts',
  PASSWORD_RESET_SUCCESSFUL = 'password reset successful',
  SELECTED_SIGN_UP_WITH_EMAIL = 'selected sign up with email',
  SELECTED_FEDERATED_SIGN_IN = 'selected federated sign in',
  LOGIN_SCREEN_VISITS = 'login screen visits',
  BC_CHECKOUT_OPEN_BAG_PAGE = 'bc_checkout_open_bag_page',
  WEBVIEW_WHITE_SCREEN = 'webview white screen',
  BRANDCLUB_SEARCH_TERM = 'brandclubSearchTerm',
  STARTED_TUTORIAL = 'started tutorial',
  COMPLETED_TUTORIAL = 'completed tutorial',
  DAILY_DEALS_PRODUCT_CARD_CLICK = 'Daily deals product card click',
  START_SURVEY = 'start_survey',
  SURVEY_FAILED = 'survey failed',
  SURVEY_RESTART = 'survey restart',
  SURVEY_CANCEL = 'survey cancel',
  COMPLETED_SURVEY = 'completed survey',
  BACK_SURVEY = 'back_survey',
  SELECTED_SURVEY_OPTION = 'selected_survey_option',
  START_CONTENT = 'start_content',
  RESTART_CONTENT = 'restart_content',
  CANCEL_CONTENT = 'cancel_content',
  BACK_CONTENT = 'back_content',
  CONTINUE_CONTENT = 'continue_content',
  FAILED_CONTENT = 'failed_content',
  COMPLETED_CONTENT = 'completed_content',
  CASH_OUT_REQUESTED = 'cash out requested',
  CASH_OUT_SUCCESS = 'cash out success',
  CASH_OUT_FAILED = 'cash out failed',
  EMPTY_CASH_OUT = 'empty_cash_out',
  SYNC_SUMMARY_NOTIFICATION = 'Sync Summary Notification',
  SEARCH = 'search',
  NO_SEARCH_RESULTS = 'no search results',
  SEARCH_ERROR = 'Search error',
  DEEPLINK_OPEN_SUCCESS = 'deeplink open success',
  DEEPLINK_OPEN_ERROR = 'deeplink open error',
  NOTIFICATION_OPEN_SUCCESS = 'notification open success',
  NOTIFICATION_OPEN_ERROR = 'notification open error',
  NOTIFICATION_USER_RESPONDED = 'notification user responded',
  OPENED_APP_LINK = 'opened app link',
  ERROR_OPENING_APP_LINK = 'error opening app link',
  ERROR_OPENING_BRANCH_APP_LINK = 'error opening branch app link',
  ERROR_PAGE = 'error page',
  WARNING = 'warning',
  ERROR = 'error',
  RETURN_PANIC = 'return panic',
  TOMBSTONE = 'tombstone: ',
  INITIATE_CHECKOUT = 'initiate_checkout',
  VIEW_BAG = 'view_bag',
  EMPTY_BAG = 'empty_bag',
  BAG_FULL = 'bag_full',
  UPDATE_BAG_QUANTITY = 'update_bag_quantity',
  CLOSE_CHECKOUT_ERROR = 'close_checkout_error',
  CHECKOUT_ERROR = 'checkout_error',
  CHECKOUT_NEXT_RETAILER = 'checkout_next_retailer',
  SKIP_CHECKOUT = 'skip_checkout',
  VIEW_CHECKOUT_SUMMARY = 'view_checkout_summary',
  REMOVE_CHECKOUT_OUT_OF_STOCK = 'remove_checkout_out_of_stock',
  CONFIRM_CHECKOUT_PARTIAL_OUT_OF_STOCK = 'confirm_checkout_partial_out_of_stock',
  ALL_OUT_OF_STOCK = 'all_out_of_stock',
  ADD_PAYMENT_METHOD = 'add_payment_method',
  DELETE_PAYMENT_METHOD = 'delete_payment_method',
  UPDATE_PAYMENT_METHOD = 'update_payment_method',
  CANCEL_UPDATE_PAYMENT_METHOD = 'cancel_update_payment_method',
  SAVE_UPDATE_PAYMENT_METHOD = 'save_update_payment_method',
  ADD_ADDRESS = 'add_address',
  DELETE_ADDRESS = 'delete_address',
  UPDATE_ADDRESS = 'update_address',
  CANCEL_UPDATE_ADDRESS = 'cancel_update_address',
  SAVE_UPDATE_ADDRESS = 'save_update_address',
  UPDATE_SECURITY = 'update_security',
  CANCEL_UPDATE_SECURITY = 'cancel_update_security',
  SAVE_UPDATE_SECURITY = 'save_update_security',
  CHECKOUT_SUCCESS = 'checkout_success',
  CANCEL_UPDATE_PROFILE = 'cancel_update_profile',
  SAVE_UPDATE_PROFILE = 'save_update_profile',
  BUY_AGAIN = 'buy_again',
  TRACK_PACKAGE = 'track_package',
  VIEW_PURCHASE = 'view_purchase',
  VIEW_PRODUCT = 'view_product',
  EMPTY_PURCHASES = 'empty_purchases',
  EMPTY_REWARDS = 'empty_rewards',
  GET_APP = 'get_app',
  REWARD_INFO = 'reward_info',
  CLOSE_HOME_MENU = 'close_home_menu',
  LANDING_PAGE_VIDEO_PLAY_STARTED = 'landingPage:videoPlayStarted',
  LANDING_PAGE_VIDEO_PLAY_PAUSED = 'landingPage:videoPlayPaused',
  LANDING_PAGE_VIDEO_PLAY_ENDED = 'landingPage:videoPlayEnded',
  PAGE_VIEW = 'page view',
  BC_USER_REQUESTED_CHECKOUT = 'bc_user_requested_checkout',
  BC_USER_ADDED_TO_BAG_FROM_URL = 'bc_user_added_to_bag_from_url',

  // Checkout V2 Events
  CHECKOUT_INTENT_CREATED = 'bc_checkout_intent_created',
  CHECKOUT_PAGE_VIEW = 'bc_checkout_page_view',
  CHECKOUT_STEP_VIEWED = 'bc_checkout_step_viewed',
  CHECKOUT_STEP_COMPLETED = 'bc_checkout_step_completed',
  CHECKOUT_STEP_ERROR = 'bc_checkout_step_error',

  CHECKOUT_RETAILER_FORGOT_PASSWORD_CLICK = 'bc_checkout_retailer_forgot_password_click',

  CHECKOUT_PAYMENT_ADD = 'bc_checkout_payment_add',
  CHECKOUT_PAYMENT_SELECT = 'bc_checkout_payment_select',

  CHECKOUT_ORDER_SUBMIT = 'bc_checkout_order_submit',
  CHECKOUT_ORDER_SUCCESS = 'bc_checkout_order_success',
  CHECKOUT_ORDER_FAILURE = 'bc_checkout_order_failure',
}
